import { useEffect, useState } from "react";
import backendApiService from "../service/backend-api.service";

const SCRIPT_SRC =
  "https://smartcaptcha.cloud.yandex.ru/captcha.js?render=onload&onload=__trendySmartCaptchaLoaded";
const SCRIPT_TIMEOUT_MS = 15000;
// Токен Яндекса живёт 5 минут и одноразовый.
const TOKEN_TTL_MS = 4 * 60 * 1000;
// challenge-hidden приходит и после решённого задания — даём callback успеть первым.
const CLOSED_GRACE_MS = 800;

export const CAPTCHA_NOTICE_URL = "https://yandex.ru/legal/smartcaptcha_notice/";
const LOAD_ERROR =
  "Не удалось загрузить проверку «Я не робот». Обновите страницу или отключите блокировщик рекламы.";
const CLOSED_ERROR = "Пройдите проверку «Я не робот», чтобы оплатить.";

let siteKey = null;
let scriptPromise = null;
let widgetId = null;
let widgetKey = null;
let pending = null;
let running = null;
let cached = null;

/**
 * Нужна ли капча этому посетителю. Сервер требует её, только когда с его IP уже
 * висит неоплаченный счёт; обычный покупатель с первым заказом Яндекс не загружает вовсе.
 */
export async function loadCaptchaStatus() {
  try {
    const data = await backendApiService.makeRequest(
      "/api/visoria/config/captcha",
      { method: "GET" },
      backendApiService.visoriaBaseURL,
    );
    siteKey = data?.required && data.siteKey ? data.siteKey : null;
  } catch {
    siteKey = null;
  }
  return siteKey;
}

function loadScript() {
  if (window.smartCaptcha) return Promise.resolve(window.smartCaptcha);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      let timer = null;
      const fail = () => {
        clearTimeout(timer);
        scriptPromise = null;
        script.remove();
        reject(new Error(LOAD_ERROR));
      };
      timer = setTimeout(fail, SCRIPT_TIMEOUT_MS);
      window.__trendySmartCaptchaLoaded = () => {
        clearTimeout(timer);
        if (window.smartCaptcha) resolve(window.smartCaptcha);
        else fail();
      };
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onerror = fail;
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

function settle(method, value) {
  const current = pending;
  pending = null;
  current?.[method](value);
}

async function ensureWidget(key) {
  const captcha = await loadScript();
  if (widgetId === null || widgetKey !== key) {
    const container = document.createElement("div");
    document.body.appendChild(container);
    widgetKey = key;
    widgetId = captcha.render(container, {
      sitekey: key,
      invisible: true,
      // Уведомление об обработке данных показываем сами под кнопкой «Оплатить».
      hideShield: true,
      callback: (token) => settle("resolve", token),
    });
    captcha.subscribe(widgetId, "challenge-hidden", () => {
      const waiting = pending;
      setTimeout(() => {
        if (pending === waiting) settle("reject", new Error(CLOSED_ERROR));
      }, CLOSED_GRACE_MS);
    });
    captcha.subscribe(widgetId, "network-error", () => settle("reject", new Error(LOAD_ERROR)));
    captcha.subscribe(widgetId, "javascript-error", () => settle("reject", new Error(LOAD_ERROR)));
  }
  return captcha;
}

async function runCaptcha() {
  const key = siteKey || (await loadCaptchaStatus());
  if (!key) return null;
  const captcha = await ensureWidget(key);
  return new Promise((resolve, reject) => {
    pending = { resolve, reject };
    captcha.reset(widgetId);
    captcha.execute(widgetId);
  });
}

function execute() {
  if (!running) {
    running = runCaptcha().finally(() => {
      running = null;
    });
  }
  return running;
}

/** Готовый токен, если он есть и не устарел; забирает его (второй раз Яндекс его не примет). */
export function takeCaptchaToken() {
  const token = cached && Date.now() - cached.at < TOKEN_TTL_MS ? cached.token : null;
  cached = null;
  return token;
}

async function getCaptchaToken() {
  const ready = takeCaptchaToken();
  if (ready) return ready;
  const token = await execute();
  cached = null;
  return token;
}

/** Заранее, пока покупатель заполняет форму: «чистый» человек получает токен без всякого задания. */
export function prefetchCaptchaToken() {
  if (!siteKey || running) return;
  if (cached && Date.now() - cached.at < TOKEN_TTL_MS) return;
  execute()
    .then((token) => {
      if (token) cached = { token, at: Date.now() };
    })
    .catch(() => {});
}

/** Обновляет только истёкший токен: после закрытого задания сама не открывает его снова. */
function refreshExpiredToken() {
  if (cached && Date.now() - cached.at >= TOKEN_TTL_MS) prefetchCaptchaToken();
}

/**
 * Отправка заказа: с готовым токеном, если он есть; если заранее начатая проверка ещё идёт —
 * дожидаемся её, а не шлём заказ впустую. Если сервер ответил CAPTCHA_REQUIRED —
 * проходим проверку (тут может показаться задание) и отправляем ещё раз.
 * onCaptcha(true) — ждём Яндекс, onCaptcha(false) — токен есть, создаём счёт.
 */
export async function sendWithCaptcha(send, onCaptcha) {
  let token = takeCaptchaToken();
  if (!token && running) {
    onCaptcha?.(true);
    token = await running.catch(() => null);
    cached = null;
    onCaptcha?.(false);
  }
  try {
    return await send(token);
  } catch (error) {
    if (error?.code !== "CAPTCHA_REQUIRED") throw error;
    onCaptcha?.(true);
    const fresh = await getCaptchaToken();
    onCaptcha?.(false);
    if (!fresh) throw error;
    return send(fresh);
  }
}

/**
 * Как только покупатель начал заполнять форму, спрашивает сервер, нужна ли капча, и если нужна —
 * готовит токен: проверка Яндекса идёт несколько секунд, пусть они пройдут до «Оплатить».
 */
export function useCaptcha(ready) {
  const [required, setRequired] = useState(false);

  useEffect(() => {
    if (!ready) return undefined;
    let alive = true;
    loadCaptchaStatus().then((key) => {
      if (!alive || !key) return;
      setRequired(true);
      prefetchCaptchaToken();
    });
    const timer = setInterval(refreshExpiredToken, 30 * 1000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [ready]);

  return required;
}
